import { EMAIL_MAX_LENGTH, INVALID_EMAIL_MESSAGE, isValidEmail, normaliseEmail } from "@somasi/shared";
import { z } from "zod";

/**
 * Zod fields for an email box, on the same rule the API enforces.
 *
 * Both trim and lowercase what they pass on, so the address the form sends is
 * the one the server would have stored anyway — and the one Cognito, which signs
 * in case-insensitively, is asked about.
 */
export const emailSchema = z
  .string()
  .trim()
  .min(1, "Enter an email address")
  .max(EMAIL_MAX_LENGTH, `An email address is at most ${EMAIL_MAX_LENGTH} characters`)
  .refine(isValidEmail, INVALID_EMAIL_MESSAGE)
  .transform((value) => value.toLowerCase());

/** As {@link emailSchema}, but blank is allowed and stays `""`. */
export const optionalEmailSchema = z
  .string()
  .trim()
  .max(EMAIL_MAX_LENGTH, `An email address is at most ${EMAIL_MAX_LENGTH} characters`)
  .refine((value) => !value || isValidEmail(value), INVALID_EMAIL_MESSAGE)
  .transform((value) => normaliseEmail(value) ?? "");

/**
 * What is wrong with an optional email box's value, or `null` when nothing is.
 * For forms that check field by field rather than through a schema.
 */
export function emailProblem(value: string | null | undefined): string | null {
  const result = optionalEmailSchema.safeParse(value ?? "");
  return result.success ? null : (result.error.issues[0]?.message ?? INVALID_EMAIL_MESSAGE);
}
